import { propertyof } from '@/utilities';
import type MatrixRow from './MatrixRow';
import { toplevelpropertyof } from '@/utilities/utils';
import type { RaceId } from '@/utilities/typealiases';

/**
 * The RaceKey used by the matrix.
 * This is the raceIds ordered ascending, dash separated
 */
export type RaceKey = string;

export type MatrixTimeDisplay = 'UsedTime' | 'ClockTime' | 'TimeAsX' | 'SplitTime';

type MatrixConfigInput = Omit<IMatrixConfig, 'TimeDisplay'> & {
    TimeDisplay?: MatrixTimeDisplay;
    ShowClockTime?: boolean;
    ShowTimeAsX?: boolean;
    ShowSplitTime?: boolean;
};

/**
 * The {@link MatrixTimeDisplay} was introduced as a replacement for the legacy ShowClockTime, ShowTimeAsX booleans when the split times was introduced.
 * This function handles the backwards compatibility, converting the legacy boolean display settings to the new MatrixTimeDisplay format.
 * @param existing The existing matrixConfig loaded from db
 * @returns The correct {@link MatrixTimeDisplay}
 */
const getMatrixTimeDisplay = (existing: MatrixConfigInput | undefined | null): MatrixTimeDisplay => {
    if (existing?.TimeDisplay) {
        return existing.TimeDisplay;
    }
    if (existing?.ShowTimeAsX) {
        return 'TimeAsX';
    }
    if (existing?.ShowClockTime) {
        return 'ClockTime';
    }
    if (existing?.ShowSplitTime) {
        return 'SplitTime';
    }
    return 'UsedTime';
};

export const buildDefaultMatrixConfig = (existing: MatrixConfigInput | undefined | null): IMatrixConfig => {
    // General columns have some columns that are selected by default.
    // This is every column except "TeamName", which is hidden by default
    let _generalColumns = existing?.GeneralColumns ?? [
        toplevelpropertyof<MatrixRow>('Bib'),
        toplevelpropertyof<MatrixRow>('StartTime'),
        toplevelpropertyof<MatrixRow>('ExitStatus'),
        toplevelpropertyof<MatrixRow>('ContestantName'),
        toplevelpropertyof<MatrixRow>('GroupName'),
        toplevelpropertyof<MatrixRow>('CategoryName'),
    ];

    return {
        RaceIDs: existing?.RaceIDs ?? null,
        RaceKey: existing?.RaceKey ?? null,
        SyntheticRaces: existing?.SyntheticRaces ?? [],
        StationRoleIDs: existing?.StationRoleIDs ?? [],
        get GeneralColumns() {
            if (this.FilterHideDNX) {
                // If we arent showing DNX, then we shouldnt show the exit status column either
                return _generalColumns.filter((col) => col !== propertyof<MatrixRow>('ExitStatus'));
            }
            return _generalColumns;
        },
        set GeneralColumns(value: Array<keyof MatrixRow>) {
            _generalColumns = value;
        },
        RaceGroupIDs: existing?.RaceGroupIDs ?? [],
        CategoryIDs: existing?.CategoryIDs ?? [],
        FilterToMissingTimes: existing?.FilterToMissingTimes ?? false,
        FilterToInTrack: existing?.FilterToInTrack ?? false,
        FilterToWarnings: existing?.FilterToWarnings ?? false,
        FilterHideDNX: existing?.FilterHideDNX ?? false,
        TimeDisplay: getMatrixTimeDisplay(existing),
        TimeDecimals: existing?.TimeDecimals ?? 'TIME',
        SpeedLimitKph: existing?.SpeedLimitKph ?? 0,
        SearchString: existing?.SearchString ?? '',
        ShowGpiStartWarning: existing?.ShowGpiStartWarning ?? true,
        DeviceStatusVisible: existing?.DeviceStatusVisible ?? true,
        BiathlonPicuresSearchWindowSecondsStart: existing?.BiathlonPicuresSearchWindowSecondsStart ?? 10,
        BiathlonPicuresSearchWindowSecondsEnd: existing?.BiathlonPicuresSearchWindowSecondsEnd ?? 60,
        HideBiathlonPictures: existing?.HideBiathlonPictures ?? false,
        SortExitStatusLast: existing?.SortExitStatusLast ?? true,
        HideContestantTimes: existing?.HideContestantTimes ?? false,
    };
};

/**
 * Represents the configuration of the matrix.
 *
 * !! Note: This should only ever be built by the {@link buildDefaultMatrixConfig} function!!
 */
export default interface IMatrixConfig {
    /**
     * Selected race IDs
     */
    RaceIDs: number[] | null;

    /**
     * This is the current race IDs sorted ascending, dash separated
     */
    RaceKey: RaceKey | null;

    /**
     * Any "synthetic" races that has been created.
     * A synthetic race is a combination of two or more real races
     */
    SyntheticRaces: Array<Array<RaceId>>;

    /**
     * Columns filter for stationRoles.
     * This has the columnIds of the stationRoles that should be shown in the matrix.
     * Note: The columnIds is created when we build the grid setup
     */
    StationRoleIDs: string[];

    /**
     * Columns filter for general columns.
     */
    GeneralColumns: Array<keyof MatrixRow>;

    /**
     * Filter rows to groups.
     */
    RaceGroupIDs: number[];

    /**
     * Filter rows to categories.
     */
    CategoryIDs: number[];

    /**
     * Row filter for missing times. This works in combination with filterToInTrack and filterToWarnings. If none is set, then all is shown.
     */
    FilterToMissingTimes: boolean;

    /**
     * Row filter for in track. This works in combination with filterToMissingTimes and filterToWarnings. If none is set, then all is shown.
     */
    FilterToInTrack: boolean;

    /**
     * Row filter for warnings. This works in combination with filterToMissingTimes and filterToInTrack. If none is set, then all is shown.
     */
    FilterToWarnings: boolean;

    /**
     * Row filter for DNX. If true, DNX is hidden
     */
    FilterHideDNX: boolean; // TODO: Is anyone ever interested in filtering the invert of this? Only show DNX?

    /**
     * How race contestant times are displayed
     */
    TimeDisplay: MatrixTimeDisplay;

    /**
     * How many decimals to show for times
     */
    TimeDecimals: 'TIME' | 'TIMEMS1' | 'TIMEMS2' | 'TIMEMS3';

    /**
     * Show warning if speed excceds the limit
     */
    SpeedLimitKph: number;

    /**
     * Whatever the user has typed into the search box
     */
    SearchString: string;

    /**
     * Show warning on start cells if the contestant doesnt have a GPI starttime
     */
    ShowGpiStartWarning: boolean;

    /**
     * Wether to show the device status view is collapsed or not
     */
    DeviceStatusVisible: boolean;

    /**
     * How many seconds after a shooting to start to look for biathlonpictures
     */
    BiathlonPicuresSearchWindowSecondsStart: number;

    /**
     * How many seconds after a shooting to end to look for biathlonpictures
     */
    BiathlonPicuresSearchWindowSecondsEnd: number;

    /**
     * If true, stops rendering the biathlonpictures
     */
    HideBiathlonPictures: boolean;

    /**
     * If true, sorts ExitStatus rows last when sorting by time.
     * If false, we use the time and ignore the ExitStatus
     */
    SortExitStatusLast: boolean;

    /**
     * If true, no contestanttimes (red times) are displayed in the matrix (they are still processed, this is display only)
     */
    HideContestantTimes: boolean;
}
